import { OutOfPocketMaxState, QmeState } from "#/lib/carrot-api/types/Subsidy"
import { Percentages } from "../hooks/useReimbursementRulePercentages"
import { PiiText } from "#/services/tracking"
import { Box, Typography } from "@carrotfertility/carotene-core"
import React, { FC } from "react"
import { FormattedMessage } from "react-intl"

type YourCostShareQmeRuleProps = {
  companyDisplayName: string
  isDefaultCategory: boolean
  getPercentages: (qme: QmeState, oop: OutOfPocketMaxState) => Percentages
}

export const YourCostShareQmeRule: FC<YourCostShareQmeRuleProps> = ({
  companyDisplayName,
  isDefaultCategory,
  getPercentages
}) => {
  const qme = getPercentages(QmeState.QME, OutOfPocketMaxState.ANY)
  const nonQme = getPercentages(QmeState.NON_QME, OutOfPocketMaxState.ANY)
  const rowColumns = "minmax(0, 1fr) 72px 72px"

  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(theme.tokens.spacing.md),
        display: "flex",
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
      })}
    >
      <Box width="100%">
        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.sm),
            marginBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%",
            borderBlockEnd: `1px solid ${theme.palette.divider}`
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            {isDefaultCategory ? (
              <FormattedMessage
                defaultMessage="Approved expenses"
                description="Table column header for expense types in the cost share table"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Approved expenses in this category"
                description="Table column header for expense types when showing a specific benefit category"
              />
            )}
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            sx={{ whiteSpace: "nowrap", textAlign: "end" }}
          >
            <FormattedMessage
              defaultMessage="You pay"
              description="Table column header for the member's cost share percentage"
            />
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            textAlign="right"
          >
            <FormattedMessage
              defaultMessage="{companyDisplayName} pays"
              description="Table column header for the employer's cost share percentage"
              values={{ companyDisplayName: <PiiText>{companyDisplayName}</PiiText> }}
            />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="QMEs"
              description="Table header abbreviation for Qualified Medical Expenses"
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: qme.memberResponsibility / 100 }}
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: qme.companyResponsibility / 100 }}
            />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="Non-QMEs"
              description="Section header abbreviation for Non-Qualified Medical Expenses"
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: nonQme.memberResponsibility / 100 }}
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: nonQme.companyResponsibility / 100 }}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
